import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { fmtBRL, fmtCompetencia, fmtDate } from '../../lib/format';
import { Search, DollarSign, RotateCcw } from 'lucide-react';

interface Pagamento {
  id: string;
  origem: string;
  valor: number;
  competencia: string;
  dataPagamento: string;
  observacao: string | null;
  parcela: {
    id: string;
    numeroParcela: number;
    valorPrevisto: number;
    status: string;
    contrato: {
      id: string;
      numeroContrato: string;
      associado: { id: string; nome: string; documentoMask: string } | null;
    };
  };
  importacaoLinhaId: string | null;
}

const origemLabel: Record<string, string> = {
  etipi: 'ETIPI', manual: 'Manual', devolucao_estorno: 'Devolução', ajuste: 'Ajuste',
};

const origemColor: Record<string, string> = {
  etipi: 'bg-blue-100 text-blue-900',
  manual: 'bg-green-100 text-green-900',
  devolucao_estorno: 'bg-amber-100 text-amber-900',
  ajuste: 'bg-purple-100 text-purple-900',
};

export function PagamentosListaPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [origem, setOrigem] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [estornando, setEstornando] = useState<Pagamento | null>(null);
  const [motivo, setMotivo] = useState('');
  const [tipoDevolucao, setTipoDevolucao] = useState<'pagamento_indevido'|'desconto_indevido'|'desistencia_pos_liquidacao'>('pagamento_indevido');
  const [errorEstorno, setErrorEstorno] = useState<string | null>(null);

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['pagamentos', { search, origem, competencia }],
    queryFn: () => {
      const params = new URLSearchParams({ size: '100' });
      if (search) params.set('q', search);
      if (origem) params.set('origem', origem);
      if (competencia) params.set('competencia', competencia);
      return api<{ items: Pagamento[]; total: number; totalValor: number }>(
        `/pagamentos/?${params.toString()}`,
      );
    },
  });

  const estornar = useMutation({
    mutationFn: (input: { pagamentoId: string; tipo: string; motivo: string }) =>
      api('/devolucoes/', { method: 'POST', body: input }),
    onSuccess: () => {
      setEstornando(null);
      setMotivo('');
      setErrorEstorno(null);
      qc.invalidateQueries({ queryKey: ['pagamentos'] });
      qc.invalidateQueries({ queryKey: ['contrato'] });
      qc.invalidateQueries({ queryKey: ['inadimplencia'] });
      qc.invalidateQueries({ queryKey: ['inadimplencia-resumo'] });
    },
    onError: (err: ApiException) => setErrorEstorno(err.apiError?.message ?? 'Falha ao estornar'),
  });

  function abrirEstorno(p: Pagamento) {
    setEstornando(p);
    setMotivo('');
    setTipoDevolucao('pagamento_indevido');
    setErrorEstorno(null);
  }

  function confirmarEstorno() {
    if (!estornando) return;
    if (motivo.trim().length < 5) {
      setErrorEstorno('Motivo deve ter pelo menos 5 caracteres');
      return;
    }
    estornar.mutate({ pagamentoId: estornando.id, tipo: tipoDevolucao, motivo: motivo.trim() });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
          Pagamentos
          <DollarSign className="h-6 w-6 text-green-500" />
        </h1>
        <p className="text-muted-foreground">Recebimentos registrados</p>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Pagamentos no filtro</CardDescription>
              <CardTitle className="text-3xl">{data.total.toLocaleString('pt-BR')}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Valor líquido</CardDescription>
              <CardTitle className={`text-3xl ${data.totalValor < 0 ? 'text-red-700' : 'text-green-700'}`}>{fmtBRL(data.totalValor)}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2 items-center flex-wrap">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por contrato, nome ou CPF…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q); }}
              className="flex-1 min-w-[280px]"
            />
            <select value={origem} onChange={(e) => setOrigem(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">Todas origens</option>
              {Object.entries(origemLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <Input type="text" placeholder="2026-04" value={competencia}
              onChange={(e) => setCompetencia(e.target.value)} className="w-32" />
            <Button variant="secondary" onClick={() => setSearch(q)}>Buscar</Button>
            {(search || origem || competencia) && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(''); setSearch(''); setOrigem(''); setCompetencia(''); }}>
                limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">Nenhum pagamento encontrado.</p>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Data</th>
                    <th className="text-left py-2 pr-3">Compet.</th>
                    <th className="text-left py-2 pr-3">Contrato</th>
                    <th className="text-left py-2 pr-3">Associado</th>
                    <th className="text-center py-2 pr-3">Parc.</th>
                    <th className="text-right py-2 pr-3">Valor</th>
                    <th className="text-left py-2 pr-3">Origem</th>
                    <th className="text-right py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => {
                    const podeEstornar = p.valor > 0 && p.origem !== 'devolucao_estorno';
                    return (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-3 text-xs">{fmtDate(p.dataPagamento)}</td>
                        <td className="py-2 pr-3">{fmtCompetencia(p.competencia)}</td>
                        <td className="py-2 pr-3">
                          <Link to={`/contratos/${p.parcela.contrato.id}`} className="text-primary hover:underline font-mono text-xs">
                            {p.parcela.contrato.numeroContrato}
                          </Link>
                        </td>
                        <td className="py-2 pr-3">
                          {p.parcela.contrato.associado && (
                            <Link to={`/associados/${p.parcela.contrato.associado.id}`} className="hover:underline">
                              <div className="text-sm">{p.parcela.contrato.associado.nome}</div>
                              <div className="text-xs text-muted-foreground font-mono">{p.parcela.contrato.associado.documentoMask}</div>
                            </Link>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-center">#{p.parcela.numeroParcela}</td>
                        <td className={`py-2 pr-3 text-right font-medium ${p.valor < 0 ? 'text-red-600' : ''}`}>{fmtBRL(p.valor)}</td>
                        <td className="py-2 pr-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${origemColor[p.origem] ?? 'bg-gray-100'}`}>
                            {origemLabel[p.origem] ?? p.origem}
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          {podeEstornar && (
                            <Button size="sm" variant="ghost" onClick={() => abrirEstorno(p)}>
                              <RotateCcw className="h-3 w-3 mr-1" />Estornar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={estornando !== null}
        onClose={() => setEstornando(null)}
        title="Estornar pagamento"
        description={estornando ? `Pagamento de ${fmtBRL(estornando.valor)} em ${fmtCompetencia(estornando.competencia)}` : ''}
      >
        {estornando && (
          <div className="space-y-4">
            <div className="text-sm bg-muted/50 rounded p-3 space-y-1">
              <div><strong>Associado:</strong> {estornando.parcela.contrato.associado?.nome}</div>
              <div><strong>Contrato:</strong> <span className="font-mono">{estornando.parcela.contrato.numeroContrato}</span></div>
              <div><strong>Parcela:</strong> #{estornando.parcela.numeroParcela} · {fmtCompetencia(estornando.competencia)}</div>
              <div><strong>Origem do pagamento:</strong> {origemLabel[estornando.origem]}</div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="tipo-devolucao">Tipo de devolução</label>
              <select id="tipo-devolucao" value={tipoDevolucao}
                onChange={(e) => setTipoDevolucao(e.target.value as any)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="pagamento_indevido">Pagamento indevido</option>
                <option value="desconto_indevido">Desconto indevido (cobrou errado)</option>
                <option value="desistencia_pos_liquidacao">Desistência pós-liquidação</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="motivo">Motivo do estorno *</label>
              <textarea id="motivo" rows={3} value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Explique brevemente por que esse pagamento está sendo estornado"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>

            <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
              <strong>Atenção:</strong> esta ação criará um pagamento negativo, reverterá a parcela para o status anterior e recalculará o status financeiro do contrato. Estornos não podem ser revertidos automaticamente.
            </div>

            {errorEstorno && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded p-3">
                {errorEstorno}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setEstornando(null)} disabled={estornar.isPending}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={confirmarEstorno} disabled={estornar.isPending}>
                {estornar.isPending ? <><Spinner className="mr-2 h-4 w-4" />estornando…</> : 'Confirmar estorno'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
